import { useState, useCallback } from 'react';
import { usePlatformAdminAuthStore } from '../../stores/platform-admin-auth.store';
import {
  systemEventsService,
  SystemEvent,
  EventStats,
  GetEventsParams,
  PaginatedEvents,
  ResolveEventDto,
  IgnoreEventDto,
} from '../../services/platform-admin/system-events.service';

export function useSystemEvents() {
  const token = usePlatformAdminAuthStore((state) => state.token);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getStats = useCallback(
    async (organizationId?: string): Promise<EventStats | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const stats = await systemEventsService.getStats(token, organizationId);
        return stats;
      } catch (err: any) {
        setError(err.message || 'Failed to fetch stats');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const getEvents = useCallback(
    async (params?: GetEventsParams): Promise<PaginatedEvents | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const result = await systemEventsService.getEvents(token, params);
        return result;
      } catch (err: any) {
        setError(err.message || 'Failed to fetch events');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const getEventById = useCallback(
    async (id: string): Promise<SystemEvent | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const event = await systemEventsService.getEventById(token, id);
        return event;
      } catch (err: any) {
        setError(err.message || 'Failed to fetch event');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const retryEvent = useCallback(
    async (id: string): Promise<SystemEvent | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const event = await systemEventsService.retryEvent(token, id);
        return event;
      } catch (err: any) {
        setError(err.message || 'Failed to retry event');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const resolveEvent = useCallback(
    async (id: string, data: ResolveEventDto): Promise<SystemEvent | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const event = await systemEventsService.resolveEvent(token, id, data);
        return event;
      } catch (err: any) {
        setError(err.message || 'Failed to resolve event');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const ignoreEvent = useCallback(
    async (id: string, data: IgnoreEventDto): Promise<SystemEvent | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const event = await systemEventsService.ignoreEvent(token, id, data);
        return event;
      } catch (err: any) {
        setError(err.message || 'Failed to ignore event');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  return {
    isLoading,
    error,
    getStats,
    getEvents,
    getEventById,
    retryEvent,
    resolveEvent,
    ignoreEvent,
  };
}
